import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BdLinkExternalComponent } from '@bindoc-projects/ui-lib/link/ui';
import { BdFeaturePermissionService } from '@bindoc-projects/bindoc-permissions-lib/data';

/**
 * Permission required to open a fully-inpatient case detail page.
 *
 * The link target (`/virtual-clinics/:id/fully-inpatient-cases/:caseId`) is
 * guarded by `BdPermissionGuard` with `failStrategy: REDIRECT`, so a user
 * without this permission who follows the link is bounced to an external
 * marketing page instead of the case detail. Hiding the link when the
 * permission is missing avoids that confusing redirect.
 */
const FULLY_INPATIENT_CASE_PERMISSION =
  'VIRTUAL_CLINIC:VIEW_FULLY_INPATIENT_CASES';

@Component({
  selector: 'ays-virtual-clinic-fully-inpatient-case-link',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (link() && hasPermission()) {
      <bd-link-external [link]="link()" label="Fall öffnen">
        <i class="pi pi-external-link"></i>
      </bd-link-external>
    }
  `,
  imports: [BdLinkExternalComponent],
})
export class AysVirtualClinicFullyInpatientCaseLinkComponent {
  readonly virtualClinicId = input<string>();
  readonly caseId = input<string>();

  private readonly _permissionService = inject(BdFeaturePermissionService);

  public readonly hasPermission = toSignal(
    this._permissionService.containsPermission([
      FULLY_INPATIENT_CASE_PERMISSION,
    ]),
    { initialValue: false },
  );

  public readonly link = computed<string | null>(() => {
    const virtualClinicId = this.virtualClinicId();
    const caseId = this.caseId();
    if (virtualClinicId && caseId) {
      return (
        '/virtual-clinics/' +
        virtualClinicId +
        '/fully-inpatient-cases/' +
        caseId
      );
    }
    return null;
  });
}
